"use client";

import React, { useState } from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import { VideoIframe, useVideo } from "@/components/components/VideoIframe";
import Image from '@/components/ui/Image';
import { cn } from "@/lib/utils";

interface Hero18Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasStatus?: boolean;
    status?: {
      value?: string;
      label?: string;
    }[];
    hasFeaturedItems?: boolean;
    featuredItems?: {
      icon?: string;
      content?: string;
    }[];
    featuredImage1?: string;
    featuredImage1AltText?: string;
    featuredItem?: "image" | "video";
    video?: string;
    loopVideo?: string;
    image?: string;
    imageAltText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero18 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero18Props) => {

  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const [videoError, setVideoError] = useState(false);

  const getVideoType = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4'; // Default fallback
    }
  };

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero18"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 18"
      removePaddingBottom={true}
      className="bg-position-[50%_100%]"  
      style={{
        backgroundImage: "url('/hero-bottom-coloured-bg.svg')",
        backgroundSize: "auto",
        backgroundRepeat: "no-repeat",
      }}
    >     
      <div className="container"> 
        <div className="w-full text-center">
          {contents?.badge && (
            <Badge variant="outline" bold className="mb-6">
              {contents?.badge}
            </Badge>
          )}
          {contents?.title && (
            <h1 className="mb-10 lg:mb-16">
              {parse(contents.title)}
            </h1>
          )}
          {contents?.content && (
            <div className="paragraph-lg mx-auto !font-normal max-w-[58ch]">
              {parse(contents?.content)}
            </div>
          )}
          {contents?.hasButton && (
            <div className="mt-8 flex flex-col">
              <div className="flex flex-col gap-2 sm:flex-row justify-center">
                <div>
                  <Button 
                    size="lg" 
                    variant={contents?.buttonStyle || "primary"}
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.buttonLink}>
                      {contents?.buttonText}
                    </a>
                  </Button>
                  {contents?.microCopy && (
                    <div className="paragraph-sm text-muted-foreground text-center mt-2">
                      {contents?.microCopy}
                    </div>
                  )}
                </div>
                {contents?.secondaryButton && (
                  <Button
                    variant={contents?.secondaryButtonStyle || "outline"}
                    size="lg"
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.secondaryButtonLink}>
                      {contents?.secondaryButtonText}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {contents?.hasStatus && (
          <div className="flex flex-col md:flex-row gap-10 items-center pt-10 md:pt-16 pb-10 justify-center">
            {contents?.status?.map((status, index) => (
              <div key={index} className="flex flex-col items-center min-w-[160px] max-w-[160px] xl:min-w-[200px] xl:max-w-[200px] text-center gap-1">
                <h3>{status.value}</h3>
                {status.label && (
                  <div className="paragraph-base text-muted-foreground">{parse(status.label)}</div>
                )}
              </div>
            ))}
          </div>
        )}
        {contents?.hasFeaturedItems && (
          <div className="flex flex-col lg:flex-row gap-8 items-center pt-10 justify-center">
            {contents?.featuredItems?.map((featuredItem, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">{featuredItem.icon}</span>
                {featuredItem.content && (
                  <p className="paragraph-lg text-muted-foreground">{parse(featuredItem.content)}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <div 
          className="w-full flex gap-6 mt-10 lg:mt-20"
        >
          <div className="w-full lg:w-[300px]">
            <div className={cn("relative", contents?.featuredItem === "video" && "cursor-pointer")} onClick={() => contents?.featuredItem === "video" && handleVideoClick(contents?.video || "")}>
              <div className="text-muted-foreground paragraph-base mb-4">How we power Australian Businesses</div>
              {contents?.loopVideo && (
                <>
                  <video 
                    preload="none" 
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    className={cn(
                      "object-cover aspect-[16/9] rounded-xl w-full h-full",
                      videoError && "hidden"
                    )}
                    onError={() => setVideoError(true)}
                  >
                    <source src={contents?.loopVideo} type={getVideoType(contents?.loopVideo)} />
                  </video>
                  <Image
                    src={contents?.image || "/placeholder.svg"}
                    alt={contents?.imageAltText || ""}
                    title={contents?.imageAltText || ""}
                    className={cn(
                      "h-full w-full object-cover aspect-[16/9] rounded-xl",
                      !videoError && "hidden"
                    )}
                    width={800}
                    height={450}
                    priority
                    fetchPriority="high"
                  />
                </>
              )}
              {contents?.image && !contents?.loopVideo && (
                <Image
                  src={contents?.featuredItem === "video" ? contents?.image || getVideoThumbnail(contents?.video || "") || '/placeholder.svg' : contents?.image || "/placeholder.svg"}
                  alt={contents?.imageAltText || ""}
                  title={contents?.imageAltText || ""}
                  className="h-full w-full object-cover aspect-[16/9] rounded-xl"
                  width={800}
                  height={450}
                  priority
                  fetchPriority="high"
                />
              )}
              {contents?.featuredItem === "video" && (
                <> 
                  <div className="absolute flex left-[16px] bottom-[16px] w-14 h-14">
                    <Button 
                      variant="secondary" 
                      size="iconLg"   
                      className="border-none bg-muted rounded-full w-14 h-14"
                    >
                      <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                    </Button>
                  </div>
                  <VideoIframe 
                    videoUrl={currentVideo} 
                    isOpen={isVideoOpen} 
                    onClose={handleCloseVideo} 
                  />
                </>
              )}
            </div>
          </div>
          {contents?.featuredImage1 && (
            <div className="hidden lg:flex flex-1">
              <Image
                src={contents?.featuredImage1}
                alt={contents?.featuredImage1AltText || ""}
                title={contents?.featuredImage1AltText || ""}
                className="h-full w-full object-contain object-bottom aspect-[990/433] rounded-xl"
                width={990}
                height={433}
                priority
                fetchPriority="high"
              />
            </div>
          )}
          
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero18 };
